import mitt, { Emitter } from "mitt";
import {
  I8thWallImageTargetEventModel,
  IMediaRecorder,
  init8thWall,
} from "./8thwall";
import { drawWatermark } from "./8thwall/watermark";
import {
  AmbientLight,
  BoxGeometry,
  DirectionalLight,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  Audio,
  Camera,
  Object3D,
  Object3DEventMap,
} from "three";
import { QRProcessEvents } from "./8thwall/qr-process-pipeline-module";
import {
  ImageTargetEvents,
  ImageTargetPipelineModuleResult,
} from "./8thwall/image-target-pipeline-module";
import { initImageTargetExperience } from "./image-targets";
import { ArtworkData, artworkData } from "./artworks";
import { initNoARScene } from "./noARScene";
import { init3dExperience } from "./placeground";
import {
  PlaceGround3dEvents,
  PlacegroundPipelineModuleResult,
} from "./8thwall/placeground-pipeline-module";
import { EJPlayer, ContentModule } from "./libs/ejx.es";
import { createContentModule } from "./createContentModule";
import {
  EJPlayerPipelineModuleEvents,
  EJPlayerPipelineModuleResult,
} from "./8thwall/ej-player-pipeline-module";
import { EJCubeProxy } from "./ejx/EJCubeProxy.js";
import { ArtworkState } from "@/context/ArtworkContext.js";
import { loadGLTF } from "./rubixContent.js";

const XR8_API_KEY =
  "5kYpgGl59W5jV3yR9ZYyetA8tUjI9wkoMPBlUnNlCpoda3HDv1NBdMigNtqxoJ5a9dPdAY";

/**
 * Events and API exposed by the renderer
 */
export type RendererEvents = {
  // When the content is loading this will update whenever the load progress changes.
  "content-load-progress": number;

  // When the content has finished loading this will be called.
  "content-loaded": void;
  // When 8thwall registers that recording has started
  "recording-started": void;
  // The recording progress, this event occurs every time a frame is captured
  "recording-progress": { elapsedSeconds: number; totalSeconds: number };
  // When 8thwall registers that recording has stopped. 8thwall will then begin encoding the vided into a real file.
  "recording-stopped": void;
  // When 8th wall has encoded the video it will send out a blob containing the video.
  "recording-ready": Blob;

  "on-show-target": I8thWallImageTargetEventModel["detail"];
  "on-hide-target": void;
  "on-update-target": (ev: I8thWallImageTargetEventModel["detail"]) => void;
  "on-targets-scanned": (ev: I8thWallImageTargetEventModel["detail"]) => void;

  "resume-tracking": void;
  "pause-tracking": void;
  "on-camera-down": boolean;
  "tracking-status": "LIMITED" | "NORMAL";
  "on-animation-loop": void;

  "content-placed": void;
  "content-repositioning": void;

  // Drag events are re-emmitted out from EJAppImmersiveARControls
  "drag-start": void;
  "drag-move": void;
  "drag-stop": void;
  // Pinch events are re-emmitted out from EJAppImmersiveARControls
  "pinch-start": void;
  "pinch-move": void;
  "pinch-stop": void;

  "on-artwork-helper-change": "left" | "right" | "none";

  "artwork-state": ArtworkState;
  "on-artwork-change": ArtworkData;

  "fake-content-found": void;
  "content-select": number;
  "animate-content-out": void;
} & QRProcessEvents &
  PlaceGround3dEvents &
  ImageTargetEvents &
  EJPlayerPipelineModuleEvents;

export type RendererApi = {
  on: Emitter<RendererEvents>["on"];
  off: Emitter<RendererEvents>["off"];
  startRecording(): void;
  stopRecording(): void;
  pauseTracking(): void;
  resumeTracking(): void;
  loadContent(artwork: ArtworkData): Promise<void>;
  clearArtwork(): void;
  repositionArtwork(): void;
  pauseAudio(): void;
  resumeAudio(): void;
  showReticle(): void;
  hideReticle(): void;
  hasBeenRepositioned: boolean;
  activateCube(): void;
  selectContent(id: number): void;
  animateContentOut(): void;
};

type RendererOptions = {
  watermarkImageUrl: string;
};

export type InitEJXCubeResult = {
  contentModule: Promise<ContentModule>;
  cubeProxy: EJCubeProxy;
  contentContainer: Object3D;
  setContent: (artwork: ArtworkData) => any;
};

const params = new URLSearchParams(window.location.search);
const noAR = params.get("noAR") === "1";
const atHome = params.get("home") === "1";

/**
 * This sets up 8thwall and placeground module and then wraps it in an API that's more specific to this project.
 */
export async function initExperienceRenderer(
  canvas: HTMLCanvasElement,
  options: RendererOptions
): Promise<RendererApi> {
  try {
    let mediaRecorder: IMediaRecorder | null;
    let module:
      | ImageTargetPipelineModuleResult
      | EJPlayerPipelineModuleEvents
      | { emitter: Emitter<RendererEvents> };
    let audio: Audio<GainNode>;
    let player: EJPlayer | undefined;
    let loadExperienceArtwork: (
      artwork: (typeof artworkData)[0]
    ) => Promise<void>;
    let clearExperienceArtwork: () => void;
    let repositionExperienceArtwork: () => void;
    let showExperienceReticle: () => void;
    let hideExperienceReticle: () => void;
    let contentHasBeenRepositioned: boolean = false;
    let resize: () => void;

    const emitter = mitt<RendererEvents>();
    if (noAR) {
      const { _player, _audio, _mediaRecorder, _resize, _module } =
        initNoARScene(canvas);

      player = _player;
      audio = _audio;
      mediaRecorder = _mediaRecorder;
      resize = _resize;
      module = _module;
    } else {
      ({ player, mediaRecorder, module, audio } = await init8thWall(
        canvas,
        XR8_API_KEY,
        options
      ));
    }

    const initEJXCube = async (artwork: ArtworkData) => {
      // Create a module loading promise
      const contentModulePromise = createContentModule(artwork.basePath);
      const { basePath } = artwork;
      
      let timeoutId: NodeJS.Timeout | null = null;
      
      const loadPromise = new Promise<void>((resolve, reject) => {
        // Set a safety timeout in case loading gets stuck
        timeoutId = setTimeout(() => {
          // console.warn(`Loading timeout for ${basePath}, forcing resolution`);
          resolve();
        }, 10000);
        
        contentModulePromise.then(contentModule => {
          player.contentKillAll();
          player.setContent({ filename: basePath, module: contentModule });
          
          const onContentLoaded = () => {
            if (player.contentLoaded()) {
              if (timeoutId) clearTimeout(timeoutId);
              emitter.off("content-loaded", onContentLoaded); // Cleanup listener
              resolve();
            }
          };
          
          emitter.on("content-loaded", onContentLoaded);
          
          if (player.contentLoaded()) {
            if (timeoutId) clearTimeout(timeoutId);
            emitter.off("content-loaded", onContentLoaded);
            resolve();
          }
        }).catch(err => {
          console.error(`Error loading content module for ${basePath}:`, err);
          if (timeoutId) clearTimeout(timeoutId);
          reject(err);
        });
      });
      
      return loadPromise;
    };

    const setEJXCube = (artwork: ArtworkData) => {
      return new Promise<void>((resolve, reject) => {
        const { basePath } = artwork;
        
        createContentModule(basePath)
          .then(contentModule => {
            player.setContent({ filename: basePath, module: contentModule });
            
            if (player.contentLoaded()) {
              resolve();
              return;
            }
            
            const timeoutId = setTimeout(() => {
              // console.warn(`setEJXCube timeout for ${basePath}, forcing resolution`);
              resolve();
            }, 8000); 
            
            const onContentLoaded = () => {
              clearTimeout(timeoutId);
              emitter.off("content-loaded", onContentLoaded);
              resolve();
            };
            
            emitter.on("content-loaded", onContentLoaded);
          })
          .catch(err => {
            console.error(`Error in setEJXCube for ${basePath}:`, err);
            reject(err);
          });
      });
    };

    let oldProgress = 0;
    player.renderOverrideFunc = () => {
      const newProgress = player.contentLoadProgress();
      if (player.contentLoaded()) {
        if (emitter && emitter.emit) emitter.emit("content-load-progress", 1);
        if (emitter && emitter.emit) emitter.emit("content-loaded");
        oldProgress = 0;
      } else if (oldProgress !== newProgress) {
        oldProgress = newProgress;
        if (emitter && emitter.emit)
          emitter.emit("content-load-progress", newProgress);
      }
    };

    console.time("initEJXCube");
    // Load all cubes in parallel rather than sequentially
    await Promise.all([
      initEJXCube(artworkData[0]),
      initEJXCube(artworkData[1]),
      initEJXCube(artworkData[2])
    ]);
    console.timeEnd("initEJXCube");

    const rubixInModel = await loadGLTF("artworks/cube/cube-in.glb");
    const rubixOutModel = await loadGLTF("artworks/cube/cube-out.glb");
    const scale = 1.05;
    rubixInModel.scene.scale.set(scale, scale, scale);
    rubixOutModel.scene.scale.set(scale, scale, scale);
    const models = [rubixInModel, rubixOutModel];

    if (atHome) {
      const {
        clearArtwork,
        repositionArtwork,
        showReticle,
        hideReticle,
        hasBeenRepositioned,
      } = init3dExperience(
        module as unknown as EJPlayerPipelineModuleResult,
        player,
        emitter,
        player.cubeContainer,
        models,
        setEJXCube
      );
      showExperienceReticle = showReticle;
      hideExperienceReticle = hideReticle;
      clearExperienceArtwork = clearArtwork;
      repositionExperienceArtwork = repositionArtwork;
      contentHasBeenRepositioned = hasBeenRepositioned;
    } else {
      initImageTargetExperience(
        module as unknown as ImageTargetPipelineModuleResult,
        player,
        emitter,
        player.cubeContainer,
        models,
        setEJXCube
      );
    }

    const moduleEmitter =
      module && (module.emitter as Emitter<Partial<RendererEvents>>);
    emitter.on("resume-tracking", () => {
      if (noAR) return;
      moduleEmitter.emit("resume-tracking");
    });
    emitter.on("pause-tracking", () => {
      if (noAR) return;
      moduleEmitter.emit("pause-tracking");
    });
    moduleEmitter?.on("artwork-state", (state) => {
      if (!state) return;
      emitter.emit("artwork-state", state);
    });
    moduleEmitter?.on("set-ui-state", (state) => {
      if (!state) return;
      emitter.emit("set-ui-state", state);
    });

    moduleEmitter?.on("on-artwork-change", (artwork) => {
      if (!artwork) return;
      emitter.emit("on-artwork-change", artwork);
    });
    emitter?.on("content-select", (id: number) => {
      moduleEmitter.emit("content-select", id);
    });
    emitter?.on("animate-content-out", () => {
      moduleEmitter.emit("animate-content-out");
    });
    emitter.on("fake-content-found", () => {
      if (!noAR) return;
      moduleEmitter.emit("fake-content-found");
    });
    // }

    const api: RendererApi = {
      on: emitter.on,
      off: emitter.off,
      /**
       *
       * @param artworkId - Artwork to load.
       */
      loadContent(artwork: ArtworkData) {
        return new Promise<void>((resolve, reject) => {
          try {
            if (!artwork) {
              console.warn(`Artwork not found`);
              reject(`Artwork not found`);
              return;
            }
            resolve();
          } catch (error) {
            console.error("Failed to load content", error);
          }
        });
      },
      clearArtwork() {
        clearExperienceArtwork();
      },

      repositionArtwork() {
        repositionExperienceArtwork();
      },
      pauseAudio() {
        if (!audio) return;
        if (audio.isPlaying) {
          audio.context.suspend();
        }
      },
      resumeAudio() {
        if (!audio) return;
        audio.context.resume();
      },
      pauseTracking() {
        this.pauseAudio();
        // if (!moduleEmitter) return;
        emitter.emit("pause-tracking");
      },
      resumeTracking() {
        this.resumeAudio();
        // if (!moduleEmitter) return;
        emitter.emit("resume-tracking");
      },
      selectContent(id: number) {
        emitter.emit("content-select", id);
      },
      animateContentOut() {
        emitter.emit("animate-content-out");
      },
      activateCube() {
        ("activatecube");
        emitter.emit("fake-content-found");
        resize();
      },

      showReticle() {
        showExperienceReticle();
      },
      hideReticle() {
        hideExperienceReticle();
      },
      hasBeenRepositioned: contentHasBeenRepositioned,
      // TODO return a promise
      startRecording() {
        if (!mediaRecorder) return;
        mediaRecorder.recordVideo({
          onStart() {
            emitter.emit("recording-started");
          },
          onStop() {
            emitter.emit("recording-stopped");
          },
          onProcessFrame(model) {
            emitter.emit("recording-progress", {
              elapsedSeconds: model.elapsedTimeMs / 1000,
              totalSeconds: model.maxRecordingMs / 1000,
            });
            drawWatermark(model.ctx);
          },
          onVideoReady(model) {
            emitter.emit("recording-ready", model.videoBlob);
          },
        });
      },
      stopRecording() {
        if (!mediaRecorder) return;
        mediaRecorder.stopRecording();
      },
    };
    return api;
  } catch (error) {
    console.error("initExperienceRenderer error", error);
    return Promise.reject(error);
  }
}
